/**
 * Search-descriptor (SD) / phrase-description (PD) post-step retry and the product journey.
 * Contract: agentic-workflow-service `docs/api/post-step-retry-contract.md`.
 * Wire shape is the domain shape — no mapper.
 */

export type PostStepName = 'SEARCH_DESCRIPTORS' | 'PHRASE_DESCRIPTIONS';

/** `GAVE_UP` = only products that failed the step `maxAttemptsPerProduct` times. */
export type PostStepScope = 'ALL' | 'FAILED' | 'MISSING' | 'NO_COVERAGE' | 'PARTIAL' | 'GAVE_UP';

/** Server default of `maxAttemptsPerProduct`, used only when an older backend does not send it. */
export const DEFAULT_MAX_ATTEMPTS = 3;

export type PostStepRetryStatus =
  | 'RUNNING'
  | 'COMPLETED'
  | 'COMPLETED_WITH_FAILURES'
  | 'FAILED'
  | 'CANCELLED';

/** A step's health on job detail. `noCoverage` is SD only; `partial` is PD only. */
export interface PostStepHealth {
  completed: number;
  failed: number;
  missing: number;
  noCoverage?: number;
  partial?: number;
  /** Distinct products a post-step retry moved to deliverable for the step. */
  fixedByRetries?: number;
  /*
   * Partition of the unhealthy products (newer backends):
   * toFix + gaveUp + accepted + noCoverageRetried + waiting.
   */
  /** Exactly what a default ("Everything") retry of this step picks — the stepper's "N to fix". */
  toFix?: number;
  /** Failed the step `maxAttemptsPerProduct` times; only the "Gave up" scope retries them. */
  gaveUp?: number;
  /** Accepted as they are; no retry picks them until undone. */
  accepted?: number;
  /** SD: a retry already found no keywords — add keywords, then retry "No keywords". */
  noCoverageRetried?: number;
  /** PD: its search descriptors cannot run (gave up, accepted, no keywords). */
  waiting?: number;
}

export interface PostStepLastRetry {
  retryId: string;
  status: PostStepRetryStatus;
  targets: number;
  recovered: number;
  stillFailed: number;
  startedAt: string | null;
  completedAt: string | null;
  redeliveredAt: string | null;
}

/** Absent when the run requested neither step; a step block is absent when it was not requested. */
export interface PostSteps {
  searchDescriptors?: PostStepHealth | null;
  phraseDescriptions?: PostStepHealth | null;
  lastRetry?: PostStepLastRetry | null;
  /** Failed retries after which a product "gave up" (server config). */
  maxAttemptsPerProduct?: number;
  /** Finished retries whose fixes were never sent, or whose redelivery failed. */
  pendingRedeliveries?: number;
}

export interface PostStepRetryRequest {
  steps?: PostStepName[];
  scope?: PostStepScope;
  productIds?: string[];
  dryRun?: boolean;
  reason?: string;
}

/** 200 (dry run) / 202 (started). `retryId` and `temporalWorkflowId` are absent on a dry run. */
export interface PostStepRetryResponse {
  jobId: string;
  retryId?: string;
  temporalWorkflowId?: string;
  dryRun: boolean;
  steps: PostStepName[];
  scope: PostStepScope;
  estimatedTargets: number;
  estimatedSearchDescriptorCalls: number;
  estimatedPhraseDescriptionCalls: number;
  /** The same counts for every scope (same steps), so one dry run fills the dialog. */
  scopes?: PostStepScopeEstimate[];
  searchDescriptors?: PostStepHealth | null;
  phraseDescriptions?: PostStepHealth | null;
}

export type PostStepRedeliveryStatus =
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'TIMED_OUT'
  | 'MOCK'
  | 'SUPPRESSED';

/** `GET …/post-step-retries` row. */
export interface PostStepRetryRun {
  retryId: string;
  status: PostStepRetryStatus;
  steps: PostStepName[];
  scope: PostStepScope;
  productFilterCount: number | null;
  targets: number;
  recovered: number;
  stillFailed: number;
  skipped: number;
  requestedBy: string | null;
  reason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  redeliveryWorkflowId: string | null;
  redeliveredAt: string | null;
  redeliveryStatus: PostStepRedeliveryStatus | null;
  redeliveryRequested: number | null;
  redeliveryDelivered: number | null;
  redeliveryFailed: number | null;
  redeliveryRunId: string | null;
  redeliveryCompletedAt: string | null;
}

export type PostStepPriorClass = 'FAILED' | 'MISSING' | 'NO_COVERAGE' | 'PARTIAL';

export type PostStepOutcome =
  | 'PENDING'
  | 'RECOVERED'
  | 'PARTIAL'
  | 'NO_COVERAGE'
  | 'STILL_FAILED'
  | 'SKIPPED_SD_FAILED'
  | 'NOT_PERSISTED';

/** `GET …/products/{productId}/post-step-attempts` row, oldest first. */
export interface PostStepRetryItem {
  retryId: string;
  productId: string;
  step: PostStepName;
  priorClass: PostStepPriorClass | null;
  priorStatus: string | null;
  priorError: string | null;
  priorRetryId: string | null;
  outcome: PostStepOutcome;
  error: string | null;
  agentBatchId: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

export interface PostStepScopeEstimate {
  scope: PostStepScope;
  targets: number;
  searchDescriptorCalls: number;
  phraseDescriptionCalls: number;
}

/** Steps the journey reads cover (Enrichment keeps today's Runs reads); `DELIVERY` = runs only. */
export type JourneyStep = PostStepName | 'DELIVERY';

/** `BATCH_DELIVERY`: the Delivery step's original run, the batch delivery it ended with. */
export type JourneyRunKind =
  | 'ENRICHMENT'
  | 'ENRICHMENT_RETRY'
  | 'POST_STEP_RETRY'
  | 'BATCH_DELIVERY';

/** A product's class in a step as a run left it. */
export type JourneyClass =
  | 'COMPLETED'
  | 'PARTIAL'
  | 'FAILED'
  | 'MISSING'
  | 'NO_COVERAGE'
  | 'WAITING'
  | 'PENDING'
  | 'READY_TO_REDELIVER'
  | 'REDELIVERED'
  | 'DELIVERED'
  | 'IN_BATCH_DELIVERY';

export type JourneyClassCounts = Partial<Record<JourneyClass, number>>;

export interface JourneyRedelivery {
  status: PostStepRedeliveryStatus | null;
  requested: number | null;
  delivered: number | null;
  failed: number | null;
}

/** One card of a step's run timeline. `seq` is 1-based over all runs of the batch. */
export interface JourneyRun {
  runId: string;
  seq: number;
  kind: JourneyRunKind;
  title: string;
  status: string | null;
  startedAt: string | null;
  total: number;
  ready: number;
  toFix: number;
  counts: JourneyClassCounts;
  /** Post-step retries only. */
  redelivery?: JourneyRedelivery | null;
  /** The batch delivery run only: the batch's delivery counters. */
  delivery?: JourneyRedelivery | null;
}

export interface JourneyProduct {
  productId: string;
  title: string | null;
  merchantId: string | null;
  /**
   * NOT_REACHED | FAILED | ENRICHED (enrichment retries), a class (post-step retries), on Delivery
   * how the batch delivery sent it (DELIVERED | IN_BATCH_DELIVERY), or null.
   */
  before: string | null;
  class: JourneyClass;
  reason: string | null;
  /** SD / PD steps: the product's class NOW (unlike `class`); actions and badges key on it. */
  liveClass?: JourneyClass | null;
  /** SD / PD steps: failed retries of this step so far (live). Null on Delivery / older backends. */
  attempts?: number | null;
  /** SD / PD steps: accepted as is and still unhealthy (live). */
  accepted?: boolean | null;
}

export interface JourneyProductPage {
  items: JourneyProduct[];
  page: number;
  size: number;
  total: number;
  /** Every class for the run and step, ignoring `class` and `q`, so the pills stay put. */
  counts: JourneyClassCounts;
}

export interface JourneyProductsQuery {
  class?: JourneyClass;
  q?: string;
  page?: number;
  size?: number;
}

/** The step's unhealthy classes a person can accept as is. */
export type PostStepAcceptClass = 'FAILED' | 'MISSING' | 'NO_COVERAGE' | 'PARTIAL';

/** `productIds`, `productClass`, `gaveUpOnly` narrow (AND); none = every unhealthy product of the step. */
export interface PostStepAcceptanceRequest {
  step: PostStepName;
  productIds?: string[];
  productClass?: PostStepAcceptClass;
  /** Only products that already failed the step `maxAttemptsPerProduct` times. */
  gaveUpOnly?: boolean;
  reason: string;
}

export interface PostStepAcceptanceUndoRequest {
  step: PostStepName;
  productIds: string[];
}

export interface PostStepAcceptanceResponse {
  /** Products accepted (or un-accepted) by this call. */
  changed: number;
}
